import { useNavigate, useParams } from 'react-router-dom';
import type { ReelDetail } from '@reelmachine/core';
import { useStableUrl } from '../lib/useStableUrl';
import { Icon } from '../ui/Icon';
import { useCurrentOrg, useReel, useReelAction } from '../api/hooks';
import { ErrorView, HistoryButton, Loading, Screen } from '../ui/Screen';
import { Stepper } from '../ui/Stepper';
import { useToast } from '../ui/Toast';
import { brandColor } from './ReelList';

function Progress({ step, progress }: { step: string; progress: number | undefined }) {
  const pct = Math.round((progress ?? 0.05) * 100);
  return (
    <div className="card" role="status">
      <p>Making the video: subtitles, brand, cuts.</p>
      <div
        className="track"
        role="progressbar"
        aria-label={step}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <i style={{ width: `${Math.max(5, pct)}%` }} />
      </div>
      <span className="nums">{step}</span>
    </div>
  );
}

const CHECK_LABEL: Record<string, string> = {
  resolution: 'Resolution',
  loudness: 'Loudness',
  'text-readable': 'Text cut off or unreadable',
  'person-in-hook': 'No person in the hook',
  'placeholder-labelled': 'Placeholder not labelled',
};

/** Result of the automatic video check: failed checks with frame numbers, or one green chip. */
export function VideoChecks({ qa }: { qa: NonNullable<ReelDetail['reel']['videoQa']> }) {
  const failed = qa.checks.filter((c) => !c.ok);
  if (qa.passed && failed.length === 0) {
    return (
      <span className="chip ok" style={{ alignSelf: 'center' }}>
        <Icon name="check" /> Video checks passed
      </span>
    );
  }
  return (
    <section className="card warnc" aria-label="Video checks">
      <h3>
        {failed.length} {failed.length === 1 ? 'check' : 'checks'} failed
      </h3>
      <ul className="qa">
        {failed.map((c, i) => (
          <li key={`${c.id}-${i}`}>
            <Icon name="alert" />
            <span>
              {c.detail || CHECK_LABEL[c.id] || c.id}
              {c.frame !== undefined && ` at frame ${c.frame}`}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Player({ detail, brand }: { detail: ReelDetail; brand: string }) {
  const videoUrl = useStableUrl(detail.videoUrl);
  if (videoUrl) {
    return (
      <div className="player">
        <video src={videoUrl} controls playsInline preload="metadata" aria-label={`Video: ${detail.reel.title}`} />
      </div>
    );
  }
  // No playable file (mock data): show the hook frame instead.
  const hook = detail.script?.lines[0];
  return (
    <div className="player">
      <div className="vf" role="img" aria-label="Video preview not available">
        <div className="band" style={{ background: `linear-gradient(${brand}, transparent)` }} />
        <div className="person" />
        <span className="tc">{detail.reel.render ? `${detail.reel.render.durationS} s` : ''}</span>
        <div className="ost">{hook?.onScreen ?? detail.reel.title}</div>
      </div>
      <p className="hint" style={{ marginTop: 8 }}>
        Preview not available for this example.
      </p>
    </div>
  );
}

export function VideoScreen() {
  const { reelId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const q = useReel(reelId);
  const { org } = useCurrentOrg();
  const render = useReelAction(reelId, 'render');
  const writePost = useReelAction(reelId, 'writePost');
  const cancel = useReelAction(reelId, 'cancelJob');

  if (q.isError) return <ErrorView back="/" error={q.error} />;
  if (q.isPending) return <Loading />;
  const detail = q.data;
  const { reel } = detail;
  const job = reel.job && (reel.job.kind === 'render' || reel.job.kind === 'transcribe') ? reel.job : undefined;
  const run = (fn: () => Promise<unknown>, to?: string) => async () => {
    try {
      await fn();
      if (to) navigate(to);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Something went wrong.');
    }
  };

  const common = { title: reel.title, back: '/', right: <HistoryButton reelId={reelId} /> };

  if (job?.status === 'running') {
    return (
      <Screen
        {...common}
        bar={
          <button
            type="button"
            className="btn sec"
            disabled={cancel.isPending}
            onClick={run(() => cancel.mutateAsync())}
          >
            {cancel.isPending ? 'Cancelling…' : 'Cancel'}
          </button>
        }
      >
        <Stepper reel={reel} current="video" />
        <Progress
          step={job.step ?? (job.kind === 'transcribe' ? 'Transcribing' : 'Rendering')}
          progress={job.progress}
        />
      </Screen>
    );
  }

  if (!reel.render) {
    return (
      <Screen
        {...common}
        bar={
          <button
            type="button"
            className="btn"
            disabled={!reel.selectedTake || render.isPending}
            onClick={run(() => render.mutateAsync())}
          >
            {job?.status === 'failed' ? 'Try again' : 'Make video'}
          </button>
        }
        barHint={reel.selectedTake ? undefined : 'Record a take first.'}
      >
        <Stepper reel={reel} current="video" />
        {job?.status === 'failed' ? (
          <div className="card warnc" role="alert">
            <h3>The video could not be made</h3>
            <p>{job.error ?? 'Something went wrong.'}</p>
          </div>
        ) : (
          <p className="muted">No video yet.</p>
        )}
      </Screen>
    );
  }

  return (
    <Screen
      {...common}
      bar={
        <button
          type="button"
          className="btn"
          disabled={writePost.isPending}
          onClick={run(() => writePost.mutateAsync(), `/reels/${reelId}/post`)}
        >
          Write post
        </button>
      }
    >
      <Stepper reel={reel} current="video" />
      {job?.status === 'failed' && (
        <div className="card warnc" role="alert">
          <h3>The last attempt failed</h3>
          <p>{job.error ?? 'Something went wrong.'}</p>
        </div>
      )}
      {detail.stale && (
        <div className="card warnc" role="note">
          <h3>Out of date</h3>
          <p>Script or take changed since this video. Make it again.</p>
          <button
            type="button"
            className="btn sec"
            disabled={render.isPending}
            onClick={run(() => render.mutateAsync())}
          >
            Make video again
          </button>
        </div>
      )}
      {reel.videoQa && <VideoChecks qa={reel.videoQa} />}
      <Player detail={detail} brand={brandColor(org)} />
      <div className="row center">
        <button type="button" className="iconbtn" onClick={() => navigate(`/reels/${reelId}/script`)}>
          Edit script
        </button>
        <button type="button" className="iconbtn" onClick={() => navigate(`/reels/${reelId}/record`)}>
          Re-record
        </button>
      </div>
    </Screen>
  );
}
